import { useSpring, animated, config, to } from '@react-spring/web';

export default function IntroAnimation({ children, delay = 0, direction = 'up', style = {}, scaleFrom = null, onRest = null, springConfig = null, ...props }) {
  // React Spring animation for fade and slide-in
  // scaleFrom: if provided, animate from this scale down to 1 (useful for logo swoop)
  // If scaleFrom is provided we assume a larger, swooping entrance (from off-screen)
  // and use viewport-relative offsets so the element appears to swoop in from the edge.
  const hasSwoop = typeof scaleFrom === 'number' && scaleFrom > 1;

  const fromTransform = hasSwoop
    ? (direction === 'up'
       ? `translateY(-50vh) scale(${scaleFrom})`
       : direction === 'down'
       ? `translateY(-50vh) scale(${scaleFrom})`
       : direction === 'left'
        ? `translateX(0vw) scale(${scaleFrom})`
        : `translateX(0vw) scale(${scaleFrom})`)
    : (direction === 'left'
        ? 'translateY(0px)'
        : direction === 'down'
       ? 'translateY(0px)'
        : direction === 'left'
        ? 'translateY(0px)'
        : 'translateX(0px)');

  // Swoop path when scaleFrom is provided: animate x/y/scale in sequence to create
  // a curved path from the corner into the header. Otherwise fall back to the simple
  // single-step translate transform used elsewhere.
  if (hasSwoop) {
    // Enter strictly from the left (not from top). Travel toward the center,
    // grow to ~500x500px midpoint, then retract back to the header final
    // position/size (56x56px).
    // midpointScale for 300px based on final header size of 56px: 300 / 56 ≈ 5.357
   // const midpointScale = 200 / 46;
    // Respect direction for the swoop path so items can enter from either side.
    const startX = direction === 'left' ? '-120vw' : '0vw';
    const midX = direction === 'left' ? '0vw' : '0vw';
    const startY = direction === 'up' ? '120vh' : '0vh';
    const midY = direction === 'up' ? '0vh' : '0vh';
  const styles = useSpring({
      // enter from the appropriate edge; keep y near the header area so motion is primarily horizontal
      from: { x: startX, y: startY, s: scaleFrom, opacity: 1 },
      to:  [
        // move toward center and grow to midpoint
        { x: midX, y: midY, //s: midpointScale,
         opacity: 0.95 },
        // retract back to final header position and size
        { x: '0px', y: '0px', s: 1, opacity: 1 },
      ],
      delay,
      // tuned spring: allow override via `springConfig` so callers can lengthen/shorten
      // the swoop without changing global defaults.
      config: springConfig || { mass: 1, tension: 150, friction: 24, precision: 0.01},
      onRest,
    });

    return (
      <animated.div
        style={{
          ...style,
          opacity: styles.opacity,
          transform: to([styles.x, styles.y, styles.s], (x, y, s) => `translate(${x}, ${y}) scale(${s})`),
        }}
        {...props}
      >
        {children}
      </animated.div>
    );
  }

  const styles = useSpring({
    from: {
      opacity: 0,
      transform: fromTransform,
    },
    to: {
      opacity: 1,
      transform: 'translate(0px, 0px)'
    },
    delay,
    config: config.gentle,
    onRest,
  });

  return (
    <animated.div style={{ ...styles, ...style }} {...props}>
      {children}
    </animated.div>
  );
}
